import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import type {
  ABTest,
  AmazonExperimentMetrics,
  ContentType,
  TestOutcome,
  TestSource,
} from "@/types/ab-tests";
import { SECTION_TITLE } from "./EditTestSectionDrawer/constants";
import { toDateInput } from "./EditTestSectionDrawer/utils";
import { ConfigurationSection } from "./EditTestSectionDrawer/ConfigurationSection";
import { ResultsSection } from "./EditTestSectionDrawer/ResultsSection";
import { ActionSection } from "./EditTestSectionDrawer/ActionSection";

export type EditableSection = "configuration" | "results" | "action";

interface EditTestSectionDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  test: ABTest;
  section: EditableSection | null;
  onSave: (patch: Partial<ABTest>) => void;
}

function buildInitialScope(test: ABTest): Set<ContentType> {
  const set = new Set<ContentType>();
  if (test.scopeFields && test.scopeFields.length > 0) {
    test.scopeFields.forEach((f) => set.add(f));
  } else {
    if (test.variantA.title || test.variantB.title) set.add("title");
    if (test.variantA.bullets && test.variantA.bullets.length > 0) {
      if (test.contentType === "bullet-set") {
        test.variantA.bullets.forEach((_, i) => set.add(`bullet-${i + 1}` as ContentType));
      } else {
        set.add(test.contentType);
      }
    }
    if (set.size === 0) set.add(test.contentType);
  }
  return set;
}

export function EditTestSectionDrawer({ open, onOpenChange, test, section, onSave }: EditTestSectionDrawerProps) {
  const [hypothesis, setHypothesis] = useState(test.hypothesis);
  const [variantATitle, setVariantATitle] = useState(test.variantA.title || "");
  const [variantBTitle, setVariantBTitle] = useState(test.variantB.title || "");
  const [variantABullets, setVariantABullets] = useState<string[]>(test.variantA.bullets || []);
  const [variantBBullets, setVariantBBullets] = useState<string[]>(test.variantB.bullets || []);
  const [startedAt, setStartedAt] = useState(toDateInput(test.startedAt));
  const [stoppedAt, setStoppedAt] = useState(toDateInput(test.stoppedAt));
  const [source, setSource] = useState<TestSource | "">(test.source ?? "");
  const [sourceDetail, setSourceDetail] = useState(test.sourceDetail ?? "");
  const [targetKeyword, setTargetKeyword] = useState(test.targetKeyword ?? "");
  const [scopeSet, setScopeSet] = useState<Set<ContentType>>(buildInitialScope(test));
  const [metrics, setMetrics] = useState<AmazonExperimentMetrics>(test.amazonMetrics || {});
  const [outcome, setOutcome] = useState<TestOutcome | "">(test.outcome ?? "");
  const [switchDate, setSwitchDate] = useState(test.variantBSwitchDate || "");

  useEffect(() => {
    if (!open) return;
    setHypothesis(test.hypothesis);
    setVariantATitle(test.variantA.title || "");
    setVariantBTitle(test.variantB.title || "");
    setVariantABullets(test.variantA.bullets || []);
    setVariantBBullets(test.variantB.bullets || []);
    setStartedAt(toDateInput(test.startedAt));
    setStoppedAt(toDateInput(test.stoppedAt));
    setSource(test.source ?? "");
    setSourceDetail(test.sourceDetail ?? "");
    setTargetKeyword(test.targetKeyword ?? "");
    setScopeSet(buildInitialScope(test));
    setMetrics(test.amazonMetrics || {});
    setOutcome(test.outcome ?? "");
    setSwitchDate(test.variantBSwitchDate || "");
  }, [open, test, section]);

  if (!section) return null;

  const testedFields: ContentType[] = Array.from(scopeSet);

  const toggleScope = (field: ContentType) => {
    setScopeSet((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  const updateVariantABullet = (idx: number, v: string) => {
    setVariantABullets((prev) => {
      const next = [...prev];
      while (next.length <= idx) next.push("");
      next[idx] = v;
      return next;
    });
  };

  const updateVariantBBullet = (idx: number, v: string) => {
    setVariantBBullets((prev) => {
      const next = [...prev];
      while (next.length <= idx) next.push("");
      next[idx] = v;
      return next;
    });
  };

  const handleSave = () => {
    if (section === "configuration") {
      const bulletFields = testedFields
        .filter((f) => f.startsWith("bullet-"))
        .map((f) => parseInt(f.split("-")[1], 10) - 1)
        .sort((a, b) => a - b);
      const aBullets = bulletFields.length
        ? bulletFields.map((i) => variantABullets[i] || (test.variantA.bullets?.[i] ?? ""))
        : undefined;
      const bBullets = bulletFields.length
        ? bulletFields.map((i) => variantBBullets[i] || (test.variantB.bullets?.[i] ?? ""))
        : undefined;
      const newContentType: ContentType =
        bulletFields.length > 1
          ? "bullet-set"
          : bulletFields.length === 1
            ? (`bullet-${bulletFields[0] + 1}` as ContentType)
            : "title";
      const toIso = (d: string) => (d ? new Date(d).toISOString() : undefined);
      onSave({
        hypothesis,
        variantA: { ...test.variantA, title: testedFields.includes("title") ? variantATitle : test.variantA.title, bullets: aBullets },
        variantB: { ...test.variantB, title: testedFields.includes("title") ? variantBTitle : test.variantB.title, bullets: bBullets },
        scopeFields: testedFields,
        contentType: newContentType,
        source: (source || undefined) as TestSource | undefined,
        sourceDetail: sourceDetail || undefined,
        targetKeyword: targetKeyword || undefined,
        startedAt: toIso(startedAt),
        stoppedAt: toIso(stoppedAt),
        startDate: startedAt || test.startDate,
        endDate: stoppedAt || test.endDate,
      });
      toast.success("Configuration updated");
    } else if (section === "results") {
      onSave({ amazonMetrics: metrics });
      toast.success("Results updated");
    } else if (section === "action") {
      onSave({
        outcome: (outcome || undefined) as TestOutcome | undefined,
        variantBSwitchDate: outcome === "variant" ? switchDate || undefined : undefined,
      });
      toast.success("Action updated");
    }
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] bg-card border-border p-0 flex flex-col gap-0">
        <div className="px-4 pt-3 pb-0">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
        <SheetHeader className="px-6 pt-2 pb-4 border-b border-border">
          <SheetTitle asChild>
            <h2 className="text-2xl font-semibold">{SECTION_TITLE[section]}</h2>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {section === "configuration" && (
            <ConfigurationSection
              startedAt={startedAt}
              stoppedAt={stoppedAt}
              source={source}
              sourceDetail={sourceDetail}
              targetKeyword={targetKeyword}
              scopeSet={scopeSet}
              hypothesis={hypothesis}
              variantATitle={variantATitle}
              variantBTitle={variantBTitle}
              variantABullets={variantABullets}
              variantBBullets={variantBBullets}
              testedFields={testedFields}
              onStartedAtChange={setStartedAt}
              onStoppedAtChange={setStoppedAt}
              onSourceChange={setSource}
              onSourceDetailChange={setSourceDetail}
              onTargetKeywordChange={setTargetKeyword}
              onToggleScope={toggleScope}
              onHypothesisChange={setHypothesis}
              onVariantATitleChange={setVariantATitle}
              onVariantBTitleChange={setVariantBTitle}
              onVariantABulletChange={updateVariantABullet}
              onVariantBBulletChange={updateVariantBBullet}
            />
          )}
          {section === "results" && (
            <ResultsSection metrics={metrics} onMetricsChange={setMetrics} />
          )}
          {section === "action" && (
            <ActionSection
              outcome={outcome}
              switchDate={switchDate}
              onOutcomeChange={setOutcome}
              onSwitchDateChange={setSwitchDate}
            />
          )}
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave}>Save</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
